import { uiText } from '../i18n/uiText';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, X, FileText, Edit2 } from 'lucide-react';
import { contractsApi, companiesApi, getApiErrorMessage } from '../api';
import PageHeader from '../components/common/PageHeader';
import ActionMenu from '../components/common/ActionMenu';
import { useConfirm } from '../store/ConfirmContext';
import {useAuth} from '../store/AuthContext';
import useRecordFormDraft from '../hooks/useRecordFormDraft';
import DraftRecoveryNotice from '../components/common/DraftRecoveryNotice';
import SaveStatus from '../components/common/SaveStatus';

const KIND_LABELS = {
  services: uiText("Договор оказания аудиторских услуг (ААС)"),
  supply: uiText("Рамочный договор поставки/подряда"),
  concession: uiText("Договор коммерческой концессии"),
  other: uiText("Иное соглашение"),
};
const STATUS_LABELS = {
  active: uiText("Действующий"),
  suspended: uiText("Приостановлен"),
  terminated: uiText("Расторгнут"),
  expired: uiText("Истёк"),
};

const emptyForm = {
  number: '', kind: 'services', subject: '',
  date_signed: '', date_effective: '', date_expires: '',
  customer_company_id: '', contractor_company_id: '',
  status: 'active', notes: '',
};

export default function ContractsPage() {
  const confirm = useConfirm();
  const {user}=useAuth();
  const draft=useRecordFormDraft(user?.id,'registry','contract',emptyForm);
  const {buffer,form,setForm,recordId:editingId}=draft;
  const busy=useRef(false);
  const [saving,setSaving]=useState(false);
  const [loaded,setLoaded]=useState(false);
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [filterKind, setFilterKind] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const load = async () => {
    setError('');
    try {
      const [contractsRes, companiesRes] = await Promise.all([
        contractsApi.list(),
        companiesApi.list(),
      ]);
      setItems(contractsRes.data || []);
      setCompanies(companiesRes.data || []);
      setLoaded(true);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(()=>{if(buffer.count) setShowForm(true);},[buffer,buffer.count]);
  const startNew=async()=>{if(busy.current)return;if(await draft.begin(null,emptyForm)){setShowForm(true);setError('');}};

  const filtered = useMemo(() => items.filter(it => {
    if (filterKind !== 'all' && it.kind !== filterKind) return false;
    if (filterStatus !== 'all' && it.status !== filterStatus) return false;
    return true;
  }), [items, filterKind, filterStatus]);

  const submit = async (e) => {
    e.preventDefault();
    if(busy.current || buffer.recovery || !loaded)return;
    if(editingId && !items.some(item=>String(item.id)===String(editingId))){setError(uiText("Договор больше не доступен. Черновик сохранён; новая запись автоматически не создаётся."));return;}
    busy.current=true;setSaving(true);
    const snapshot=buffer.snapshot();
    setError('');
    try {
      const payload = {
        ...form,
        customer_company_id: form.customer_company_id ? Number(form.customer_company_id) : null,
        contractor_company_id: form.contractor_company_id ? Number(form.contractor_company_id) : null,
      };
      if (editingId) await contractsApi.update(editingId, payload);
      else await contractsApi.create(payload);
      if(draft.saved(snapshot)) setShowForm(false);
      if(buffer.active) await load();
    } catch (e2) { if(buffer.active) setError(getApiErrorMessage(e2)); }
    finally{busy.current=false;setSaving(false);}
  };

  const startEdit = async(item) => {
    if(busy.current)return;
    if(!await draft.begin(item.id,{
      number: item.number || '',
      kind: item.kind || 'services',
      subject: item.subject || '',
      date_signed: item.date_signed || '',
      date_effective: item.date_effective || '',
      date_expires: item.date_expires || '',
      customer_company_id: item.customer_company_id || '',
      contractor_company_id: item.contractor_company_id || '',
      status: item.status || 'active',
      notes: item.notes || '',
    }))return;
    setError('');
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (id) => {
    if (!await confirm(uiText("Удалить договор?"), { title: uiText("Удаление договора"), confirmLabel: uiText("Удалить") })) return;
    try {
      await contractsApi.delete(id);
      load();
    } catch (e) { setError(getApiErrorMessage(e)); }
  };

  return (
    <div className="page-content">
      <PageHeader
        icon={FileText}
        title={uiText("Договоры — основания аудитов")}
        subtitle={uiText("Реестр коммерческих договоров. По регламенту аудит проводится только при наличии договорного основания.")}
        right={
          <button type="button" className="btn btn-primary" disabled={saving} onClick={startNew}>
            <Plus size={15} />{uiText(" Добавить договор")}</button>
        }
      />

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      <DraftRecoveryNotice buffer={buffer} label={draft.recoveryId ? uiText("договор №{v0}", {v0: (draft.recoveryId)}) : uiText("новый договор")} disabled={saving || !loaded}/>
      <SaveStatus saving={saving} error={error} recovery={buffer.recovery} dirty={buffer.count>0}/>
      {!showForm && (buffer.count>0 || editingId) && <button type="button" className="ap-button ap-button--secondary" disabled={saving || !!buffer.recovery} onClick={()=>setShowForm(true)}>{uiText("Продолжить договор")}</button>}

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <div className="card-title">{editingId ? uiText("Редактирование договора") : uiText("Новый договор")}</div>
            <button type="button" className="btn btn-ghost btn-sm" aria-label={uiText("Свернуть форму договора")} disabled={saving} onClick={() => setShowForm(false)}><X size={16} />{uiText(" Свернуть форму")}</button>
          </div>
          <form onSubmit={submit}>
            <div className="card-body"><fieldset className="ap-form-lock" disabled={saving || !!buffer.recovery || !loaded}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">{uiText("Номер договора")}</label>
                  <input className="form-control" value={form.number} onChange={e => setForm(p => ({ ...p, number: e.target.value }))} required placeholder={uiText("№ Д-2026/041")} />
                </div>
                <div className="form-group">
                  <label className="form-label required">{uiText("Тип договора")}</label>
                  <select className="form-control" value={form.kind} onChange={e => setForm(p => ({ ...p, kind: e.target.value }))}>
                    {Object.entries(KIND_LABELS)
                      .filter(([k]) => ['services', 'other'].includes(k) || (editingId && k === items.find(item => item.id === editingId)?.kind))
                      .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{uiText("Статус")}</label>
                  <select className="form-control" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Предмет договора")}</label>
                <input className="form-control" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder={uiText("Например: оказание услуг по аудиту контрагентов")} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{uiText("Дата подписания")}</label>
                  <input type="date" className="form-control" value={form.date_signed} onChange={e => setForm(p => ({ ...p, date_signed: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">{uiText("Действует с")}</label>
                  <input type="date" className="form-control" value={form.date_effective} onChange={e => setForm(p => ({ ...p, date_effective: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">{uiText("Действует по")}</label>
                  <input type="date" className="form-control" value={form.date_expires} onChange={e => setForm(p => ({ ...p, date_expires: e.target.value }))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{uiText("Заказчик")}</label>
                  <select className="form-control" value={form.customer_company_id} onChange={e => setForm(p => ({ ...p, customer_company_id: e.target.value }))}>
                    <option value="">{uiText("Не указан")}</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{uiText("Исполнитель / Контрагент")}</label>
                  <select className="form-control" value={form.contractor_company_id} onChange={e => setForm(p => ({ ...p, contractor_company_id: e.target.value }))}>
                    <option value="">{uiText("Не указан")}</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{uiText("Примечания")}</label>
                <textarea className="form-control" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} />
              </div>
            </fieldset></div>
            <div className="card-footer contract-editor-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-outline" disabled={saving} onClick={() => setShowForm(false)}>{uiText("Свернуть форму")}</button>
              <button type="submit" className="btn btn-primary" disabled={saving || !!buffer.recovery || !loaded}>{saving?uiText("Сохранение…"):editingId ? uiText("Сохранить") : uiText("Создать")}</button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title">{uiText("Реестр договоров (")}{filtered.length}{uiText(" из ")}{items.length})</div>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            <button type="button" className={`btn btn-sm ${filterKind === 'all' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilterKind('all')}>{uiText("Все типы")}</button>
            {Object.entries(KIND_LABELS).map(([k, v]) => (
              <button type="button" key={k} className={`btn btn-sm ${filterKind === k ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilterKind(k)}>{v}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            <button type="button" className={`btn btn-sm ${filterStatus === 'all' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilterStatus('all')}>{uiText("Все статусы")}</button>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <button type="button" key={k} className={`btn btn-sm ${filterStatus === k ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilterStatus(k)}>{v}</button>
            ))}
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{uiText("Номер")}</th>
                  <th>{uiText("Тип")}</th>
                  <th>{uiText("Стороны")}</th>
                  <th>{uiText("Период")}</th>
                  <th>{uiText("Статус")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(item => (
                  <tr key={item.id}>
                    <td><strong>{item.number}</strong>{item.subject && <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{item.subject}</div>}</td>
                    <td>{KIND_LABELS[item.kind] || item.kind}</td>
                    <td style={{ fontSize: 12 }}>
                      {item.customer_company_name && <div>{uiText("Заказчик: ")}<b>{item.customer_company_name}</b></div>}
                      {item.contractor_company_name && <div>{uiText("Исполнитель: ")}<b>{item.contractor_company_name}</b></div>}
                    </td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                      {item.date_signed && <div>{uiText("Подписан: ")}{item.date_signed}</div>}
                      {item.date_expires && <div>{uiText("Истекает: ")}{item.date_expires}</div>}
                    </td>
                    <td><span className="ui-badge ui-badge-neutral">{STATUS_LABELS[item.status] || item.status}</span></td>
                    <td>
                      <div className="row-primary-action">
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => startEdit(item)}><Edit2 size={13} />{uiText(" Изменить")}</button>
                      <ActionMenu
                        label={uiText("Другие действия с договором {v0}", {v0: (item.number)})}
                        actions={[{ key: 'delete', label: uiText("Удалить договор"), danger: true, onClick: () => remove(item.id) }]}
                      />
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="6" style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                    {items.length === 0 ? uiText("Реестр договоров пуст") : uiText("Под текущие фильтры не найдено")}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
